import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { auth } from "../firebase";
import config from "../config";
import "../styles/PlayerFeedbackView.css";

function PlayerFeedbackView({
  gameId, // Use this instead of useParams if provided
  currentRound,
  isSubmitting, // Feedback submission in progress (from parent)
  submitError, // Feedback submission error (from parent)
  onSubmitFeedback, // Function to call for submission (to parent)
  hasSubmittedFeedback, // Boolean indicating if feedback was already submitted this round (from parent)
}) {
  const { gameId: paramGameId } = useParams();
  const effectiveGameId = gameId || paramGameId;

  const [feedbackData, setFeedbackData] = useState({
    submissions: [],
    myResources: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allocations, setAllocations] = useState({});

  const fetchFeedbackData = useCallback(async () => {
    if (!effectiveGameId) return;
    console.log(
      `PlayerFeedbackView: Fetching feedback data for game ${effectiveGameId}`
    );
    setIsLoading(true);
    setError(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("Authentication required.");

      const response = await axios.get(
        `${config.apiUrl}/api/games/${effectiveGameId}/feedback`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("PlayerFeedbackView: Received feedback data:", response.data);
      setFeedbackData({
        submissions: response.data.submissions || [],
        myResources: response.data.myResources || [],
      });
    } catch (err) {
      console.error("Error fetching feedback data:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to load feedback data."
      );
      setFeedbackData({ submissions: [], myResources: [] });
    } finally {
      setIsLoading(false);
    }
  }, [effectiveGameId]);

  useEffect(() => {
    fetchFeedbackData();
  }, [effectiveGameId, currentRound, fetchFeedbackData]);

  useEffect(() => {
    console.log(
      "PlayerFeedbackView: Resetting allocations due to data/prop change."
    );
    setAllocations({});
  }, [feedbackData, hasSubmittedFeedback]);

  const handleAllocationChange = (submissionId, categoryName, value) => {
    const amount = parseInt(value, 10);
    const key = `${submissionId}-${categoryName}`;

    if (hasSubmittedFeedback) return;

    if (!isNaN(amount) && amount >= 0) {
      setAllocations((prev) => ({ ...prev, [key]: amount }));
    } else if (value === "") {
      setAllocations((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
    }
  };

  const calculateRemainingResources = () => {
    const myResources = feedbackData.myResources || [];
    if (myResources.length === 0) return {};
    const remaining = myResources.reduce((acc, res) => {
      acc[res.category_name] = res.resource_count;
      return acc;
    }, {});

    for (const key in allocations) {
      // eslint-disable-next-line no-unused-vars
      const [submissionId, categoryName] = key.split("-");
      const amount = allocations[key] || 0;
      if (remaining[categoryName] !== undefined) {
        remaining[categoryName] -= amount;
      }
    }
    return remaining;
  };

  const handleSubmitAllocations = async () => {
    const currentRemaining = calculateRemainingResources();
    if (Object.values(currentRemaining).some((r) => r < 0)) {
      alert("Cannot submit: You have overspent resources.");
      return;
    }

    if (
      Object.keys(allocations).length === 0 ||
      Object.values(allocations).every((v) => v === 0)
    ) {
      alert("Cannot submit: Please allocate at least one resource.");
      return;
    }

    if (isSubmitting || hasSubmittedFeedback) return;

    const payloadAllocations = [];
    for (const key in allocations) {
      const amount = allocations[key];
      if (amount > 0) {
        const [submissionIdStr, categoryName] = key.split("-");
        payloadAllocations.push({
          submissionId: parseInt(submissionIdStr, 10),
          categoryName: categoryName,
          amount: amount,
        });
      }
    }

    if (payloadAllocations.length === 0) {
      alert("Cannot submit: No valid allocations were entered.");
      return;
    }

    console.log("Calling onSubmitFeedback (from parent) with payload:", {
      allocations: payloadAllocations,
    });

    if (onSubmitFeedback) {
      onSubmitFeedback(payloadAllocations);
    } else {
      console.error("onSubmitFeedback prop is not provided!");
      alert("Configuration error: Cannot submit feedback.");
    }
  };

  if (isLoading) return <p>Loading feedback data...</p>;
  if (error) return <p className="error">Error loading data: {error}</p>;

  const remainingResources = calculateRemainingResources();
  const submissionsToDisplay = feedbackData.submissions || [];
  const resourcesToDisplay = feedbackData.myResources || [];

  return (
    <div className="feedback-container">
      <div className="feedback-header">
        <h2>Round {currentRound} - Resource Allocation</h2>
      </div>

      <div className="feedback-section">
        <h3>Your Resources</h3>
        {resourcesToDisplay.length === 0 ? (
          <p>No resource information available.</p>
        ) : (
          <div className="resources-container">
            {resourcesToDisplay.map((res) => (
              <div key={res.category_name} className="resource-item">
                <span className="resource-name">{res.category_name}</span>
                <div className="resource-values">
                  <span
                    className={`resource-remaining ${
                      remainingResources[res.category_name] < 0
                        ? "negative"
                        : ""
                    }`}
                  >
                    {remainingResources[res.category_name] ??
                      res.resource_count}
                  </span>
                  <span className="resource-separator">/</span>
                  <span className="resource-total">{res.resource_count}</span>
                  {remainingResources[res.category_name] < 0 && (
                    <span className="overspent">(Overspent!)</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <h3>Allocate Resources to Submissions</h3>
      {hasSubmittedFeedback ? (
        <p className="success-message">✔ Feedback submitted for this round.</p>
      ) : submissionsToDisplay.length === 0 ? (
        <p>No submissions available for feedback allocation.</p>
      ) : (
        submissionsToDisplay.map((sub) => (
          <div
            key={sub.id}
            className={`submission-card ${
              hasSubmittedFeedback ? "disabled" : ""
            }`}
          >
            <p className="submission-text">{sub.text_content}</p>
            <div className="categories-container">
              {Array.isArray(sub.categories) &&
                sub.categories.map((cat) => (
                  <div key={cat} className="category-input-group">
                    <label htmlFor={`${sub.id}-${cat}`}>{cat}:</label>
                    <input
                      type="number"
                      id={`${sub.id}-${cat}`}
                      min="0"
                      value={allocations[`${sub.id}-${cat}`] || ""}
                      onChange={(e) =>
                        handleAllocationChange(sub.id, cat, e.target.value)
                      }
                      className="input"
                      disabled={isSubmitting || hasSubmittedFeedback}
                    />
                  </div>
                ))}
            </div>
          </div>
        ))
      )}

      {!hasSubmittedFeedback && submissionsToDisplay.length > 0 && (
        <>
          <button
            onClick={handleSubmitAllocations}
            disabled={
              Object.values(remainingResources).some((r) => r < 0) ||
              isSubmitting ||
              isLoading ||
              hasSubmittedFeedback
            }
            className="button"
          >
            {isSubmitting ? "Submitting..." : "Submit Allocations"}
          </button>
          {submitError && <p className="error">Error: {submitError}</p>}
        </>
      )}
    </div>
  );
}

PlayerFeedbackView.defaultProps = {
  isSubmitting: false,
  submitError: null,
  onSubmitFeedback: () => console.warn("onSubmitFeedback prop missing!"),
  hasSubmittedFeedback: false,
};

export default PlayerFeedbackView;
